import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { useAuthStore } from '@/features/auth/stores/auth.store'
import { env } from '@/shared/config/env'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/features/auth/views/LoginView.vue'),
    meta: { title: '登录', public: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'overview',
        component: () => import('@/features/overview/views/OverviewView.vue'),
        meta: { title: '概览', description: '掌握平台核心指标与运行状态' },
      },
      {
        path: 'orders',
        name: 'orders',
        component: () => import('@/features/orders/views/OrdersView.vue'),
        meta: { title: '订单与退款', description: '查询订单并提交退款申请' },
      },
      {
        path: 'refunds',
        name: 'refunds',
        component: () => import('@/features/refunds/views/RefundsView.vue'),
        meta: { title: '退款审批', description: '复核退款申请并触发原路退款' },
      },
      {
        path: 'invoices',
        name: 'invoices',
        component: () => import('@/features/invoices/views/InvoicesView.vue'),
        meta: { title: '开票审批', description: '核对用户抬头、实际消费金额与订单' },
      },
      {
        path: 'reconciliation',
        name: 'reconciliation',
        component: () => import('@/features/reconciliation/views/ReconciliationView.vue'),
        meta: { title: '余额对账', description: '核对充值到账与已消耗的充值余额并导出账单' },
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/features/users/views/UsersView.vue'),
        meta: { title: '用户管理', description: '查询用户并执行受保护的账户操作' },
      },
      {
        path: 'models',
        name: 'models',
        component: () => import('@/features/models/views/ModelsView.vue'),
        meta: { title: '模型管理', description: '维护模型元数据并审核公开发布与版本变更' },
      },
      {
        path: 'subscriptions',
        name: 'subscriptions',
        component: () => import('@/features/subscriptions/views/SubscriptionsView.vue'),
        meta: { title: '会员套餐', description: '管理套餐版本与销售状态' },
      },
      {
        path: 'promotions',
        name: 'promotions',
        component: () => import('@/features/promotions/views/PromotionsView.vue'),
        meta: { title: '特惠定价', description: '配置模型特惠范围、折扣比例与有效期' },
      },
      {
        path: 'contracts',
        name: 'contracts',
        component: () => import('@/features/contracts/views/ContractsView.vue'),
        meta: { title: '合约管理', description: '起草、签署并管理创作者合约' },
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: () => import('@/features/notifications/views/NotificationsView.vue'),
        meta: { title: '通知消息', description: '维护通知正文、类型与用户可见范围' },
      },
      {
        path: 'nodes',
        name: 'nodes',
        component: () => import('@/features/nodes/views/NodesView.vue'),
        meta: { title: '计算节点', description: '管理计算节点状态与访问授权' },
      },
      {
        path: 'inmine-keys',
        name: 'inmine-keys',
        component: () => import('@/features/inmine-keys/views/InmineKeysView.vue'),
        meta: { title: 'Inmine 密钥', description: '管理上游密钥库存、额度与状态' },
      },
      {
        path: 'configurations',
        name: 'configurations',
        component: () => import('@/features/configurations/views/ConfigurationsView.vue'),
        meta: { title: '配置项', description: '维护运行参数与启用状态' },
      },
      {
        path: 'components',
        name: 'components',
        component: () => import('@/features/system/views/ComponentsView.vue'),
        meta: { title: '组件预览', description: '后台通用设计组件与交互状态' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/features/system/views/NotFoundView.vue'),
    meta: { title: '页面不存在', public: true },
  },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  await auth.initialize()

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.name === 'login' && auth.isAuthenticated) return { name: 'overview' }

  return true
})

router.afterEach((route) => {
  document.title = route.meta.title ? route.meta.title + ' - ' + env.appTitle : env.appTitle
})
